import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Send, CheckCircle2 } from "lucide-react";

/**
 * FloatingFahad
 *
 * Placeholder for the future AI assistant. Renders as a small Saudi-styled
 * bot avatar at the bottom-left of every portal page. Two modes:
 *
 *  1. Welcome bubble — auto-shown once per language per session, says
 *     "أسفرت وأنورت" + a short intro.
 *  2. Chat panel — opened on click; the user types a free-text inquiry and
 *     it's POSTed to `/api/portal/fahad/inquiry`. Managers + STAS get a
 *     notification, and the user sees an "official reply incoming" confirmation.
 */
const GREETINGS = {
  ar: {
    line1: "أسفرت وأنورت",
    line2: "أنا فهد، مساعدك الذكي. اكتب رقم معاملتك وأطلعك على حالتها فوراً، أو أرسل لي استفسارك.",
    placeholder: "اكتب استفسارك هنا…",
    name_lbl: "اسمك (اختياري)",
    mobile_lbl: "رقم الجوال (اختياري)",
    app_lbl: "رقم المعاملة",
    app_hint: "اكتب رقم معاملتك لتعرف حالتها فوراً.",
    check_btn: "اطّلع على حالة معاملتي",
    not_found: "لم أتعرّف على هذا الرقم. تأكّد منه أو اكتب لي استفسارك ليصلك الرد.",
    welcome_named: "حياك الله عزيزي {name} 👋",
    welcome_anon: "حياك الله عزيزنا الكريم 👋",
    appt_intro: "تفاصيل موعدك:",
    appt_date: "التاريخ",
    appt_time: "الوقت",
    appt_city: "المدينة",
    ask_more: "هل تحتاج شيء آخر؟",
    escalate_btn: "أحتاج التواصل مع خدمة العملاء",
    send: "إرسال إلى خدمة العملاء",
    sending: "جاري الإرسال…",
    success_title: "تم استلام استفسارك",
    success_body: "جاري التحويل إلى خدمة العملاء، سيصلك إشعار رسمي عند الرد.",
    chat_title: "محادثة فهد",
    chat_sub: "مساعدك الذكي — استعلام مباشر",
    short_msg: "اكتب رسالة أطول لو سمحت.",
    err: "تعذّر الإرسال، حاول مرة أخرى.",
    looking: "لحظة من فضلك…",
  },
  en: {
    line1: "Your presence brightens our day",
    line2: "I'm Fahad, your smart assistant. Enter your case number for instant status, or send your inquiry.",
    placeholder: "Type your question here…",
    name_lbl: "Your name (optional)",
    mobile_lbl: "Mobile number (optional)",
    app_lbl: "Case number",
    app_hint: "Enter your case number to see its status instantly.",
    check_btn: "Check my case status",
    not_found: "I couldn't find this number. Please verify it or write your question and we'll reply.",
    welcome_named: "Welcome dear {name} 👋",
    welcome_anon: "Welcome, valued client 👋",
    appt_intro: "Your appointment details:",
    appt_date: "Date",
    appt_time: "Time",
    appt_city: "City",
    ask_more: "Anything else I can help with?",
    escalate_btn: "I need to talk to customer service",
    send: "Send to customer service",
    sending: "Sending…",
    success_title: "Your inquiry was received",
    success_body: "We're routing your message to customer service. You'll get an official reply notification soon.",
    chat_title: "Chat with Fahad",
    chat_sub: "Your smart assistant — instant status",
    short_msg: "Please write a longer message.",
    err: "Send failed, please try again.",
    looking: "One moment…",
  },
  ur: {
    line1: "آپ کی تشریف آوری ہمارے لیے باعثِ خوشی ہے",
    line2: "میں فہد ہوں، آپ کا اسمارٹ معاون۔ فوری حالت کے لیے اپنا کیس نمبر درج کریں، یا اپنا سوال بھیجیں۔",
    placeholder: "اپنا سوال یہاں لکھیں…",
    name_lbl: "آپ کا نام (اختیاری)",
    mobile_lbl: "موبائل نمبر (اختیاری)",
    app_lbl: "کیس نمبر",
    app_hint: "فوری حالت دیکھنے کے لیے اپنا کیس نمبر درج کریں۔",
    check_btn: "میرے کیس کی حالت دیکھیں",
    not_found: "یہ نمبر نہیں ملا۔ تصدیق کریں یا اپنا سوال لکھیں، ہم جلد جواب دیں گے۔",
    welcome_named: "خوش آمدید عزیز {name} 👋",
    welcome_anon: "خوش آمدید محترم گاہک 👋",
    appt_intro: "آپ کی اپائنٹمنٹ کی تفصیل:",
    appt_date: "تاریخ",
    appt_time: "وقت",
    appt_city: "شہر",
    ask_more: "کیا مزید کوئی مدد چاہیے؟",
    escalate_btn: "مجھے کسٹمر سروس سے بات کرنی ہے",
    send: "کسٹمر سروس کو بھیجیں",
    sending: "بھیجا جا رہا ہے…",
    success_title: "آپ کا پیغام موصول ہو گیا",
    success_body: "ہم آپ کا پیغام کسٹمر سروس کو منتقل کر رہے ہیں۔ جلد ہی آپ کو سرکاری جواب کا اشعار موصول ہوگا۔",
    chat_title: "فہد سے گفتگو",
    chat_sub: "آپ کا اسمارٹ معاون — فوری استفسار",
    short_msg: "براہ کرم لمبا پیغام لکھیں۔",
    err: "بھیجنا ناکام، دوبارہ کوشش کریں۔",
    looking: "ایک لمحہ…",
  },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api/portal`;

export default function FloatingFahad({ theme, dir, lang }) {
  const t = GREETINGS[lang] || GREETINGS.ar;
  const isDark = theme.name === "dark";

  // UI state: "closed" | "bubble" | "chat" | "success" | "status"
  const [state, setState] = useState("closed");
  const [msg, setMsg] = useState("");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [appNum, setAppNum] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);   // { found, client_name, status_text, appointment }
  const [lookupBusy, setLookupBusy] = useState(false);
  // Free-form Q&A transcript (deterministic, no LLM). Each item:
  // { id, role: "user"|"bot", text, source?, faqId?, handoff? }
  const [thread, setThread] = useState([]);
  const inputRef = useRef(null);

  // Auto-open the bubble once per language per session
  useEffect(() => {
    const seenKey = `vta-fahad-greeted-${lang}`;
    const alreadyGreeted = typeof window !== "undefined" && sessionStorage.getItem(seenKey);
    if (!alreadyGreeted) {
      const showTimer = setTimeout(() => setState("bubble"), 900);
      const hideTimer = setTimeout(() => {
        setState((s) => (s === "bubble" ? "closed" : s));
        try { sessionStorage.setItem(seenKey, "1"); } catch (_) { /* private mode */ }
      }, 8400);
      return () => { clearTimeout(showTimer); clearTimeout(hideTimer); };
    }
  }, [lang]);

  // Focus the textarea once the chat panel opens
  useEffect(() => {
    if (state === "chat" && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [state]);

  const palette = {
    bubbleBg: isDark ? "rgba(30,26,18,0.96)" : "rgba(255,253,247,0.98)",
    bubbleBorder: theme.borderAccent,
    bubbleText: isDark ? "#f3eccb" : "#1f1d18",
    bubbleSub: isDark ? "#b9b29a" : "#5a5340",
    avatarRing: theme.borderAccent,
    avatarBg: isDark ? "#1a1610" : "#fbf7eb",
  };

  const submit = async () => {
    setErr("");
    const userMessage = msg.trim();
    if (userMessage.length < 2) {
      setErr(t.short_msg);
      return;
    }
    // Optimistically append the user's message to the thread.
    const userTurn = {
      id: `u-${Date.now()}`,
      role: "user",
      text: userMessage,
    };
    setThread((prev) => [...prev, userTurn]);
    setMsg("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/fahad/ask`, {
        message: userMessage,
        client_name: name.trim() || null,
        client_mobile: mobile.trim() || null,
        application_number: appNum.trim().toUpperCase() || null,
        lang,
      });
      const botTurn = {
        id: `b-${Date.now()}`,
        role: "bot",
        text: data?.answer || "",
        source: data?.source,
        faqId: data?.matched_faq_id,
        handoff: !!data?.needs_handoff,
        context: data?.context || null,
      };
      setThread((prev) => [...prev, botTurn]);
      // If Fahad resolved a tracking number, also surface the rich status
      // panel underneath so the client sees their full case snapshot.
      if (data?.source === "app_lookup" && data?.context?.found) {
        setLookupResult({
          found: true,
          client_name: data.context.client_name,
          client_mobile: data.context.client_mobile,
          application_number: data.context.application_number,
          status_code: data.context.status_code,
          status_text: data.context.status_text,
          appointment: data.context.appointment,
          invoice: data.context.invoice,
          missing_documents: data.context.missing_documents,
        });
      }
    } catch (e) {
      setErr(t.err);
    } finally {
      setBusy(false);
    }
  };

  /** Smart lookup: try to resolve the case number to a status + appointment.
   * Updates `lookupResult` so the chat panel can render an inline reply
   * without forwarding anything to a human. */
  const lookupCase = async () => {
    setErr("");
    if (!appNum.trim()) return;
    setLookupBusy(true);
    try {
      const { data } = await axios.post(`${API}/fahad/lookup`, {
        application_number: appNum.trim().toUpperCase(),
      });
      setLookupResult(data);
      setState("status");
    } catch (e) {
      setLookupResult({ found: false });
      setState("status");
    } finally {
      setLookupBusy(false);
    }
  };

  return (
    <div
      data-testid="floating-fahad"
      style={{
        position: "fixed",
        left: "20px",
        bottom: "20px",
        zIndex: 40,
        display: "flex",
        alignItems: "flex-end",
        gap: "12px",
        pointerEvents: "none",
        maxWidth: "calc(100vw - 32px)",
      }}
    >
      {state === "bubble" && (
        <WelcomeBubble t={t} palette={palette} dir={dir} theme={theme}
          onClose={() => setState("closed")}
          onChat={() => setState("chat")} />
      )}

      {state === "chat" && (
        <ChatPanel
          t={t} palette={palette} dir={dir} theme={theme} lang={lang}
          msg={msg} setMsg={setMsg}
          name={name} setName={setName}
          mobile={mobile} setMobile={setMobile}
          appNum={appNum} setAppNum={setAppNum}
          err={err} busy={busy}
          lookupBusy={lookupBusy} onLookup={lookupCase}
          thread={thread}
          inputRef={inputRef}
          onClose={() => setState("closed")}
          onSubmit={submit}
        />
      )}

      {state === "status" && (
        <StatusPanel
          t={t} palette={palette} dir={dir} theme={theme} lang={lang}
          result={lookupResult}
          onClose={() => setState("closed")}
          onEscalate={() => setState("chat")}
          onTryAgain={() => { setLookupResult(null); setState("chat"); }}
        />
      )}

      {state === "success" && (
        <SuccessPanel t={t} palette={palette} dir={dir} theme={theme}
          onClose={() => setState("closed")} />
      )}

      {/* Avatar — always rendered, click toggles chat */}
      <button
        data-testid="fahad-avatar"
        onClick={() => {
          if (state === "chat" || state === "success") setState("closed");
          else setState("chat");
        }}
        aria-label="Fahad"
        style={{
          pointerEvents: "auto",
          width: "62px",
          height: "62px",
          borderRadius: "50%",
          background: palette.avatarBg,
          border: `2px solid ${palette.avatarRing}`,
          padding: 0,
          cursor: "pointer",
          boxShadow: `0 10px 28px rgba(0,0,0,0.28), 0 0 0 4px ${isDark ? "rgba(212,175,55,0.08)" : "rgba(166,124,26,0.08)"}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          animation: state === "closed" ? "vta-fahad-float 5s ease-in-out infinite" : "none",
        }}
      >
        <SaudiBot size={54} />
      </button>

      <style>{`
        @keyframes vta-fahad-bubble-in {
          from { opacity: 0; transform: translateY(8px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes vta-fahad-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-4px); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="fahad-avatar"] { animation: none; }
        }
      `}</style>
    </div>
  );
}

/* ───────────────────────── Welcome bubble ───────────────────────── */
function WelcomeBubble({ t, palette, dir, theme, onClose, onChat }) {
  return (
    <div
      dir={dir}
      data-testid="fahad-welcome-bubble"
      style={{
        pointerEvents: "auto",
        background: palette.bubbleBg,
        backdropFilter: "blur(8px)",
        color: palette.bubbleText,
        border: `1px solid ${palette.bubbleBorder}`,
        borderRadius: "18px 18px 18px 4px",
        padding: "14px 16px 14px 18px",
        maxWidth: "280px",
        fontFamily: "'Tajawal','Segoe UI',sans-serif",
        boxShadow: "0 14px 36px rgba(0,0,0,0.25)",
        position: "relative",
        animation: "vta-fahad-bubble-in 360ms cubic-bezier(0.4,0,0.2,1) both",
      }}
    >
      <button
        data-testid="fahad-bubble-close"
        onClick={onClose}
        aria-label="close"
        style={{
          position: "absolute", top: 6, insetInlineEnd: 6,
          background: "transparent", border: "none",
          color: palette.bubbleSub, cursor: "pointer", padding: 2, lineHeight: 0,
        }}
      >
        <X size={14} />
      </button>
      <div style={{ fontWeight: 800, fontSize: "14px", lineHeight: 1.45, marginBottom: "6px", color: theme.gold }}>
        {t.line1}
      </div>
      <div style={{ fontSize: "12.5px", lineHeight: 1.7, color: palette.bubbleSub, marginBottom: "10px" }}>
        {t.line2}
      </div>
      <button
        onClick={onChat}
        data-testid="fahad-open-chat"
        style={{
          padding: "7px 14px", borderRadius: "999px",
          background: theme.gold, color: "#1a1410", border: "none",
          fontWeight: 800, fontSize: "12px", cursor: "pointer",
        }}
      >
        {t.send}
      </button>
    </div>
  );
}

/* ───────────────────────── Chat panel ───────────────────────── */
function ChatPanel({ t, palette, dir, theme, lang, msg, setMsg, name, setName, mobile, setMobile, appNum, setAppNum, err, busy, lookupBusy, onLookup, thread, inputRef, onClose, onSubmit }) {
  const css = {
    box: {
      pointerEvents: "auto",
      width: "340px",
      maxWidth: "calc(100vw - 110px)",
      background: palette.bubbleBg,
      backdropFilter: "blur(10px)",
      border: `1px solid ${palette.bubbleBorder}`,
      borderRadius: "18px 18px 18px 4px",
      boxShadow: "0 16px 40px rgba(0,0,0,0.32)",
      padding: "14px 14px 12px",
      fontFamily: "'Tajawal','Segoe UI',sans-serif",
      animation: "vta-fahad-bubble-in 280ms cubic-bezier(0.4,0,0.2,1) both",
    },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" },
    titleWrap: { display: "flex", flexDirection: "column", lineHeight: 1.2 },
    title: { fontSize: "13.5px", fontWeight: 900, color: theme.gold },
    titleSub: { fontSize: "10.5px", color: palette.bubbleSub, marginTop: "2px", letterSpacing: "0.2px" },
    close: { background: "transparent", border: "none", color: palette.bubbleSub, cursor: "pointer", padding: 2, lineHeight: 0 },
    inputRow: { display: "flex", gap: "6px", marginBottom: "8px" },
    inputSmall: {
      flex: 1, padding: "8px 10px", borderRadius: "8px",
      background: "transparent", color: palette.bubbleText,
      border: `1px solid ${theme.borderSoft}`, fontSize: "12px",
      fontFamily: "inherit", outline: "none",
    },
    appWrap: {
      marginBottom: "8px",
      padding: "8px 10px",
      borderRadius: "10px",
      background: "rgba(212,175,55,0.06)",
      border: `1px dashed ${theme.borderAccent}`,
    },
    appLabel: { fontSize: "11px", color: theme.gold, fontWeight: 700, marginBottom: "4px" },
    appHint: { fontSize: "10.5px", color: palette.bubbleSub, marginTop: "5px", lineHeight: 1.5 },
    appInput: {
      width: "100%", padding: "7px 10px", borderRadius: "8px",
      background: "transparent", color: palette.bubbleText,
      border: `1px solid ${theme.borderSoft}`, fontSize: "12.5px",
      fontFamily: "inherit", outline: "none", letterSpacing: "1px",
      textAlign: "center",
    },
    textarea: {
      width: "100%", minHeight: "84px", padding: "10px 12px", borderRadius: "10px",
      background: "transparent", color: palette.bubbleText,
      border: `1px solid ${theme.borderSoft}`, fontSize: "13px",
      lineHeight: 1.6, resize: "vertical", fontFamily: "inherit", outline: "none",
    },
    err: { color: theme.errorText || "#ff6b6b", fontSize: "11.5px", marginTop: "6px" },
    submit: {
      marginTop: "10px", width: "100%",
      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px",
      padding: "10px 14px", borderRadius: "12px",
      background: theme.gold, color: "#1a1410", border: "none",
      fontWeight: 800, fontSize: "13px", cursor: busy ? "not-allowed" : "pointer",
      opacity: busy ? 0.7 : 1,
    },
  };
  return (
    <div dir={dir} style={css.box} data-testid="fahad-chat-panel">
      <div style={css.header}>
        <div style={css.titleWrap}>
          <div style={css.title}>{t.chat_title}</div>
          <div style={css.titleSub}>{t.chat_sub}</div>
        </div>
        <button onClick={onClose} style={css.close} aria-label="close" data-testid="fahad-chat-close">
          <X size={16} />
        </button>
      </div>

      {/* Application-number block — Fahad's hero CTA. Lookup-first. */}
      <div style={css.appWrap}>
        <div style={css.appLabel}>{t.app_lbl}</div>
        <input
          style={css.appInput}
          placeholder="VTA-26-XXXXXXXX"
          value={appNum}
          onChange={(e) => setAppNum(e.target.value.toUpperCase())}
          maxLength={40}
          dir="ltr"
          data-testid="fahad-chat-app"
        />
        <div style={css.appHint}>{t.app_hint}</div>
        <button
          type="button"
          onClick={onLookup}
          disabled={lookupBusy || !appNum.trim()}
          style={{
            marginTop: "8px", width: "100%",
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px",
            padding: "8px 12px", borderRadius: "10px",
            background: "transparent", color: theme.gold,
            border: `1.4px solid ${theme.borderAccent}`,
            fontWeight: 800, fontSize: "12.5px",
            cursor: lookupBusy || !appNum.trim() ? "not-allowed" : "pointer",
            opacity: lookupBusy || !appNum.trim() ? 0.55 : 1,
          }}
          data-testid="fahad-chat-lookup"
        >
          {lookupBusy ? t.looking : t.check_btn}
        </button>
      </div>

      {/* Transcript — appears as soon as the client sends their first message */}
      {thread && thread.length > 0 && (
        <div
          data-testid="fahad-chat-transcript"
          dir={dir}
          style={{
            maxHeight: "200px",
            overflowY: "auto",
            margin: "8px 0",
            padding: "8px 4px",
            borderTop: `1px solid ${theme.borderSoft}`,
            borderBottom: `1px solid ${theme.borderSoft}`,
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          {thread.map((turn) => (
            <div
              key={turn.id}
              data-testid={`fahad-turn-${turn.role}`}
              style={{
                alignSelf: turn.role === "user" ? (dir === "rtl" ? "flex-start" : "flex-end") : (dir === "rtl" ? "flex-end" : "flex-start"),
                maxWidth: "85%",
                padding: "7px 10px",
                borderRadius: turn.role === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
                background: turn.role === "user"
                  ? (palette.bubbleSub === "#5a5340" ? "rgba(212,175,55,0.15)" : "rgba(212,175,55,0.18)")
                  : "rgba(120,120,120,0.08)",
                border: `1px solid ${turn.handoff ? (theme.borderAccent || theme.gold) : theme.borderSoft}`,
                color: palette.bubbleText,
                fontSize: "12.5px",
                lineHeight: 1.6,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {turn.text}
              {turn.role === "bot" && turn.handoff && (
                <div style={{
                  marginTop: "4px",
                  fontSize: "10.5px",
                  color: theme.gold,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}>
                  <CheckCircle2 size={12} strokeWidth={2.4} />
                  {t.success_title}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={css.inputRow}>
        <input
          style={css.inputSmall} placeholder={t.name_lbl}
          value={name} onChange={(e) => setName(e.target.value)}
          maxLength={120} dir={dir} data-testid="fahad-chat-name"
        />
        <input
          style={css.inputSmall} placeholder={t.mobile_lbl}
          value={mobile} onChange={(e) => setMobile(e.target.value)}
          maxLength={40} dir="ltr" data-testid="fahad-chat-mobile"
        />
      </div>
      <textarea
        ref={inputRef}
        style={css.textarea} placeholder={t.placeholder}
        value={msg} onChange={(e) => setMsg(e.target.value)}
        maxLength={600} dir={dir} data-testid="fahad-chat-message"
      />
      {err && <div style={css.err} data-testid="fahad-chat-error">{err}</div>}
      <button
        type="button" onClick={onSubmit} disabled={busy}
        style={css.submit} data-testid="fahad-chat-submit"
      >
        <Send size={14} strokeWidth={2.4} />
        {busy ? t.sending : t.send}
      </button>
    </div>
  );
}

/* ───────────────────────── Success panel ───────────────────────── */
function SuccessPanel({ t, palette, dir, theme, onClose }) {
  return (
    <div
      dir={dir}
      style={{
        pointerEvents: "auto",
        background: palette.bubbleBg,
        backdropFilter: "blur(10px)",
        border: `1px solid ${theme.success || theme.borderAccent}`,
        borderRadius: "18px 18px 18px 4px",
        padding: "14px 18px",
        maxWidth: "320px",
        boxShadow: "0 16px 40px rgba(0,0,0,0.32)",
        fontFamily: "'Tajawal','Segoe UI',sans-serif",
        animation: "vta-fahad-bubble-in 280ms cubic-bezier(0.4,0,0.2,1) both",
        position: "relative",
      }}
      data-testid="fahad-success-panel"
    >
      <button
        onClick={onClose}
        aria-label="close"
        style={{
          position: "absolute", top: 6, insetInlineEnd: 6,
          background: "transparent", border: "none", color: palette.bubbleSub,
          cursor: "pointer", padding: 2, lineHeight: 0,
        }}
        data-testid="fahad-success-close"
      >
        <X size={14} />
      </button>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        color: theme.gold, fontWeight: 900, fontSize: "14px", marginBottom: "6px",
      }}>
        <CheckCircle2 size={18} strokeWidth={2.4} />
        {t.success_title}
      </div>
      <div style={{ fontSize: "12.5px", lineHeight: 1.7, color: palette.bubbleSub }}>
        {t.success_body}
      </div>
    </div>
  );
}

/* ───────────────────────── Status panel ───────────────────────── */
/** Inline reply when Fahad successfully resolves a tracking number. Renders
 * a warm welcome by the client's name + the status line + appointment block
 * if scheduled. The client can ask for more or escalate to a human. */
function StatusPanel({ t, palette, dir, theme, lang, result, onClose, onEscalate, onTryAgain }) {
  const found = !!result?.found;
  const name = result?.client_name?.trim();
  const greeting = found && name
    ? t.welcome_named.replace("{name}", name)
    : t.welcome_anon;
  const statusText = (result?.status_text || {})[lang] || (result?.status_text || {}).ar;
  const appt = result?.appointment;

  const css = {
    box: {
      pointerEvents: "auto",
      width: "340px",
      maxWidth: "calc(100vw - 110px)",
      background: palette.bubbleBg,
      backdropFilter: "blur(10px)",
      border: `1px solid ${palette.bubbleBorder}`,
      borderRadius: "18px 18px 18px 4px",
      boxShadow: "0 16px 40px rgba(0,0,0,0.32)",
      padding: "14px 16px 14px",
      fontFamily: "'Tajawal','Segoe UI',sans-serif",
      animation: "vta-fahad-bubble-in 280ms cubic-bezier(0.4,0,0.2,1) both",
      position: "relative",
    },
    close: { position: "absolute", top: 6, insetInlineEnd: 6, background: "transparent", border: "none", color: palette.bubbleSub, cursor: "pointer", padding: 2, lineHeight: 0 },
    greeting: { fontSize: "14px", fontWeight: 900, color: theme.gold, marginBottom: "8px" },
    status: { fontSize: "13.5px", lineHeight: 1.7, color: palette.bubbleText, marginBottom: "10px" },
    apptBox: { marginTop: "8px", padding: "10px 12px", borderRadius: "10px", background: "rgba(212,175,55,0.08)", border: `1px solid ${theme.borderAccent}` },
    apptTitle: { fontSize: "11.5px", color: theme.gold, fontWeight: 800, marginBottom: "6px" },
    apptRow: { display: "flex", justifyContent: "space-between", fontSize: "12.5px", padding: "2px 0", color: palette.bubbleText },
    apptLbl: { color: palette.bubbleSub, fontWeight: 600 },
    apptVal: { fontWeight: 800, letterSpacing: "0.3px" },
    actions: { display: "flex", gap: "8px", marginTop: "12px" },
    btnGhost: {
      flex: 1, padding: "9px 10px", borderRadius: "10px",
      background: "transparent", color: theme.text,
      border: `1px solid ${theme.borderSoft}`,
      fontWeight: 700, fontSize: "12px", cursor: "pointer",
    },
    btnPrimary: {
      flex: 1, padding: "9px 10px", borderRadius: "10px",
      background: theme.gold, color: "#1a1410", border: "none",
      fontWeight: 800, fontSize: "12px", cursor: "pointer",
    },
    notFound: { fontSize: "13px", color: palette.bubbleText, lineHeight: 1.7 },
  };

  return (
    <div dir={dir} style={css.box} data-testid="fahad-status-panel">
      <button onClick={onClose} aria-label="close" style={css.close} data-testid="fahad-status-close">
        <X size={16} />
      </button>

      {found ? (
        <>
          <div style={css.greeting} data-testid="fahad-greeting">{greeting}</div>
          <div style={css.status} data-testid="fahad-status-text">{statusText}</div>
          {appt && (appt.date || appt.time) && (
            <div style={css.apptBox} data-testid="fahad-appointment">
              <div style={css.apptTitle}>{t.appt_intro}</div>
              {appt.date && (
                <div style={css.apptRow}>
                  <span style={css.apptLbl}>{t.appt_date}</span>
                  <span style={css.apptVal} dir="ltr">{appt.date}</span>
                </div>
              )}
              {appt.time && (
                <div style={css.apptRow}>
                  <span style={css.apptLbl}>{t.appt_time}</span>
                  <span style={css.apptVal} dir="ltr">{appt.time}</span>
                </div>
              )}
              {appt.city && (
                <div style={css.apptRow}>
                  <span style={css.apptLbl}>{t.appt_city}</span>
                  <span style={css.apptVal}>{appt.city}</span>
                </div>
              )}
            </div>
          )}
          <div style={css.actions}>
            <button onClick={onTryAgain} style={css.btnGhost} data-testid="fahad-status-back">
              {t.ask_more}
            </button>
            <button onClick={onEscalate} style={css.btnPrimary} data-testid="fahad-status-escalate">
              {t.escalate_btn}
            </button>
          </div>
        </>
      ) : (
        <>
          <div style={css.greeting}>{t.welcome_anon}</div>
          <div style={css.notFound} data-testid="fahad-not-found">{t.not_found}</div>
          <div style={css.actions}>
            <button onClick={onTryAgain} style={css.btnPrimary} data-testid="fahad-status-retry">
              {t.escalate_btn}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ───────────────────────── Saudi Bot SVG ───────────────────────── */
function SaudiBot({ size = 54 }) {
  const FACE = "#f4e3c0";
  const FACE_SHADE = "#d9c69a";
  const GHUTRA = "#fbf7ef";
  const GHUTRA_EDGE = "#d8cdb0";
  const IGAL = "#1a1610";
  const IGAL_HL = "#3a322a";
  const EYE = "#b88f1f";
  const MOUTH = "#6a4f1c";
  const CHEEK = "rgba(190, 100, 60, 0.18)";

  return (
    <svg viewBox="0 0 64 64" width={size} height={size} fill="none">
      <path d="M14 24 Q 10 38, 14 52 Q 18 56, 22 54 L 22 30 Q 16 22, 14 24 Z"
        fill={GHUTRA} stroke={GHUTRA_EDGE} strokeWidth="0.7" strokeLinejoin="round" />
      <path d="M50 24 Q 54 38, 50 52 Q 46 56, 42 54 L 42 30 Q 48 22, 50 24 Z"
        fill={GHUTRA} stroke={GHUTRA_EDGE} strokeWidth="0.7" strokeLinejoin="round" />
      <rect x="17" y="20" width="30" height="28" rx="12" fill={FACE} stroke={FACE_SHADE} strokeWidth="0.7" />
      <path d="M12 22 Q 32 8, 52 22 L 52 28 Q 32 22, 12 28 Z"
        fill={GHUTRA} stroke={GHUTRA_EDGE} strokeWidth="0.7" strokeLinejoin="round" />
      <ellipse cx="32" cy="21" rx="17" ry="2.6" fill={IGAL} />
      <ellipse cx="32" cy="20" rx="17" ry="0.8" fill={IGAL_HL} opacity="0.7" />
      <rect x="23" y="32" width="5" height="2.6" rx="1.1" fill={EYE} />
      <rect x="36" y="32" width="5" height="2.6" rx="1.1" fill={EYE} />
      <ellipse cx="24" cy="40" rx="2" ry="1" fill={CHEEK} />
      <ellipse cx="40" cy="40" rx="2" ry="1" fill={CHEEK} />
      <path d="M27 42.5 Q 32 45 37 42.5" stroke={MOUTH} strokeWidth="1.4" fill="none" strokeLinecap="round" />
    </svg>
  );
}
